// src/pages/Profile.jsx
import { Box, Typography, Divider, Switch, FormControlLabel } from '@mui/material';
import CardManager from '../components/CardManager';
import { styled } from '@mui/material/styles';
import { useContext, useEffect, useState } from 'react';
import { UserContext } from '../contexts/UserContext';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import Settings from '../components/Settings';

const BG_GRADIENT = 'linear-gradient(180deg, #000 0%, #0a0a0a 100%)';
const GOLD = '#d4af37';
const WARM = '#f9f6e3';

const GoldSwitch = styled(Switch)(({ theme }) => ({
    '& .MuiSwitch-switchBase.Mui-checked': { color: GOLD },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(212,175,55,0.6)' },
    '& .MuiSwitch-track': { backgroundColor: 'rgba(212,175,55,0.25)' },
}));

function Profile() {
    const { userData } = useContext(UserContext);
    const uid = userData?.uid || getAuth().currentUser?.uid || '';
    // ✅ Local state mirrors Firestore and auto-refreshes via onSnapshot
    const [allowTrade, setAllowTrade] = useState(userData?.privacy?.allowTradeBrowse ?? true)  // Live subscription to user doc for freshest allowTradeBrowse on mount/nav to /profile
    useEffect(() => {
        if (!uid) { setAllowTrade(true); return; } // safe default
        const ref = doc(db, 'users', uid);
        const unsub = onSnapshot(
            ref,
            (snap) => {
                const val = snap.data()?.privacy?.allowTradeBrowse;
                setAllowTrade(val === undefined ? true : !!val);
            },
            () => setAllowTrade(true) // on error, keep usable default
        ); return () => unsub()
    }, [uid]);

    const onToggle = async (checked) => {
        if (!uid) return;
        const prev = allowTrade;
        setAllowTrade(checked); // optimistic
        try {
            await updateDoc(doc(db, 'users', uid), { 'privacy.allowTradeBrowse': !!checked });
        } catch (e) {
            setAllowTrade(prev); // revert on failure
            // optional: surface an error toast/snackbar here if you have a global notifier
            // e.g., enqueueSnackbar(e?.message || 'Failed to update setting', { variant: 'error' });
        }
    };

    return (
        <>
            <Typography
                variant="h3"
                sx={{
                    fontFamily: `'Cinzel', serif`,
                    letterSpacing: '0.8px',
                    fontWeight: 700,
                    color: WARM,
                    textShadow: '0 0 12px rgba(212,175,55,0.4)',
                }}
                gutterBottom
            >
                Profile
            </Typography>

            <Typography
                sx={{
                    color: WARM,
                    opacity: 0.9,
                    mb: 1,
                }}
            >
                Manage your deck and view your owned cards.
            </Typography>
            <Settings />
            <Divider
                sx={{
                    borderColor: GOLD,
                    opacity: 0.6,
                    boxShadow: `0 0 8px rgba(212,175,55,0.4)`,
                }}
            />

            {/* Privacy / Trading toggle */}
            <Box
                sx={{
                    mt: 2,
                    p: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    border: '1px solid rgba(212,175,55,0.25)',
                    borderRadius: 2,
                    bgcolor: 'rgba(0,0,0,0.7)',
                    boxShadow: '0 0 24px rgba(212,175,55,0.15)',
                }}
            >
                <Box>
                    <Typography
                        sx={{ fontFamily: `'Cinzel', serif`, color: WARM, letterSpacing: '0.6px' }}
                    >
                        Allow Trade Offers
                    </Typography>
                    <Typography variant="body2" sx={{ color: WARM, opacity: 0.85 }}>
                        Turn off to prevent players from sending offers or browsing your tradables.
                    </Typography>
                </Box>
                <FormControlLabel
                    control={
                        <GoldSwitch
                            checked={!!allowTrade}
                            onChange={(e) => onToggle(e.target.checked)}
                        />
                    }
                    label={allowTrade ? 'On' : 'Off'}
                    sx={{ color: WARM, ml: 1, '& .MuiFormControlLabel-label': { fontFamily: `'Cinzel', serif` } }}
                />
            </Box>

            {/* Main section */}
            <CardManager />
        </>
    );
}

export default Profile;
